/**
 * Helpers for rendering a user's "role + status badges".
 *
 * The data model has a single role per user, but two independent signals
 * indicate "unlimited exports":
 *   1. The user's role slug is `subscriber` (or holds `billing.unlimited`),
 *      i.e. an admin manually granted permanent unlimited access.
 *   2. The user has an active Culqi subscription with a future expiry.
 *
 * `RoleBadges` renders the base role pill PLUS a "Suscriptor" badge when
 * either signal is true. The badge tooltip explains *why* it's showing.
 */

import { Infinity as InfinityIcon } from 'lucide-react';
import type { AuthUser } from '@/stores/authStore';

function isSubscriptionActive(
  status: string | null | undefined,
  expiresAt: string | null | undefined,
): boolean {
  if (status !== 'active' || !expiresAt) return false;
  return new Date(expiresAt).getTime() > Date.now();
}

/** True if the user's role grants "billing.unlimited" (the seeded
 *  `subscriber` role, or admin via `modules.all`). */
function hasUnlimitedRole(user: Pick<AuthUser, 'role'>): boolean {
  const perms = user.role?.permissions ?? [];
  if (user.role?.slug === 'subscriber') return true;
  return perms.includes('billing.unlimited');
}

interface RoleBadgesProps {
  user: Pick<AuthUser, 'role' | 'subscription_status' | 'subscription_expires_at'>;
  /** When true, the badges sit on a row (default). When false, they stack. */
  inline?: boolean;
}

export function RoleBadges({ user, inline = true }: RoleBadgesProps) {
  const subActive = isSubscriptionActive(user.subscription_status, user.subscription_expires_at);
  const unlimitedRole = hasUnlimitedRole(user);
  // Only show the "Suscriptor" badge as a SEPARATE pill when the role
  // doesn't already say "Suscriptor". Otherwise it would duplicate.
  const showBadge = subActive && user.role?.slug !== 'subscriber';
  const tooltip = subActive
    ? `Suscripción activa hasta ${user.subscription_expires_at?.slice(0, 10) ?? ''}`
    : unlimitedRole
      ? 'Rol con exportaciones ilimitadas'
      : '';

  return (
    <div className={inline ? 'inline-flex flex-wrap items-center gap-1.5' : 'flex flex-col gap-1'}>
      <span
        className="rounded-[var(--radius-pill)] border px-2 py-0.5 text-[10.5px]"
        style={{
          background: unlimitedRole ? 'rgba(245,158,11,0.18)' : 'var(--surface-2)',
          borderColor: unlimitedRole ? 'rgba(245,158,11,0.4)' : 'var(--border-default)',
          color: unlimitedRole ? 'var(--accent)' : 'var(--text-primary)',
        }}
        title={tooltip || undefined}
      >
        {user.role.label}
      </span>
      {showBadge && (
        <span
          className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10.5px] font-medium"
          style={{ background: 'rgba(245,158,11,0.18)', color: 'var(--accent)' }}
          title={tooltip}
        >
          <InfinityIcon size={10} strokeWidth={2} aria-hidden />
          Suscriptor
        </span>
      )}
    </div>
  );
}
